import React, { useState } from 'react';
import { Shield, ChevronDown, ChevronRight } from 'lucide-react';
import HelpIconButton from '../HelpIconButton';

// Collapsible four-section layout: Roadmap, Controls, Evidence, Attestations.
// The Roadmap section starts expanded so the current milestone is visible
// on first load. Placeholder content throughout.

type SectionKey = 'roadmap' | 'controls' | 'evidence' | 'attestations';

const TSGSecurityPosture: React.FC = () => {
  const [expanded, setExpanded] = useState<Record<SectionKey, boolean>>({
    roadmap: true,
    controls: false,
    evidence: false,
    attestations: false,
  });

  const toggle = (key: SectionKey) =>
    setExpanded(prev => ({ ...prev, [key]: !prev[key] }));

  const Section: React.FC<{
    sectionKey: SectionKey;
    title: string;
    children: React.ReactNode;
  }> = ({ sectionKey, title, children }) => {
    const isOpen = expanded[sectionKey];
    return (
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <button
          onClick={() => toggle(sectionKey)}
          className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
        >
          <span className="text-sm font-semibold text-gray-900">{title}</span>
          {isOpen ? (
            <ChevronDown className="w-4 h-4 text-gray-500" />
          ) : (
            <ChevronRight className="w-4 h-4 text-gray-500" />
          )}
        </button>
        {isOpen && (
          <div className="px-4 pb-4 pt-2 text-sm text-gray-700 leading-relaxed border-t border-gray-100">
            {children}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-emerald-100 rounded-lg">
            <Shield className="w-6 h-6 text-emerald-700" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">TSG Security Posture</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              SOC 2 Type II roadmap, control families, evidence, and attestations.
            </p>
          </div>
        </div>
        <HelpIconButton screenId="tsg-security-posture" showLabel />
      </div>

      <div className="space-y-3">
        <Section sectionKey="roadmap" title="SOC 2 Type II Roadmap">
          <ol className="list-decimal pl-5 space-y-1.5">
            <li>Gap assessment complete.</li>
            <li>Control design in progress — current milestone.</li>
            <li>Observation window begins — planned.</li>
            <li>Type II audit — planned.</li>
          </ol>
          <p className="mt-3 text-xs text-gray-500">
            Current milestone and dates are placeholder until the approved roadmap lands.
          </p>
        </Section>

        <Section sectionKey="controls" title="Control Families">
          <p>
            Covered control families include: Access Management, Change Management,
            Incident Response, Vulnerability Management, Business Continuity, Vendor
            Management, and Data Protection. Each family maps to a set of evidence items
            in the internal Evidence Collection module.
          </p>
        </Section>

        <Section sectionKey="evidence" title="Evidence Library">
          <p>
            TSG maintains an internal evidence library that is continuously refreshed by
            the Evidence Collection module in Jarvis. External reviewers do not have
            direct access to the evidence library — see the Requesting Full Audit Reports
            section for the three pathways available to request specific artifacts.
          </p>
        </Section>

        <Section sectionKey="attestations" title="Attestations">
          <p>
            Most recent attestation: placeholder. The current status badge in the portal
            header reflects the latest auditor signoff. Historical attestations and their
            scope are available on request.
          </p>
        </Section>
      </div>

      <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg text-xs text-gray-500">
        Placeholder content — will be replaced with approved text from
        <span className="font-mono mx-1">jarvis-trust-portal-help-text.docx</span>
        when it lands.
      </div>
    </div>
  );
};

export default TSGSecurityPosture;
